// Werkstatt kartındaki "⚠ Problem" butonuna tıklanınca açılan modal
import { useState } from 'react';
import { api, type ProblemKategorie, type ProblemPriority } from '../lib/api';

interface Props {
  modul: 'drueckteile'|'laufraeder'|'baugruppen'|'montage';
  recordId: number;
  bestellnr: string;
  kundeName?: string | null;
  userId: number | null;           // Werkstatt'ta seçilen abteilung user'ı
  abteilungId: number | null;
  onClose: () => void;
  onSaved?: () => void;
}

const KATEGORIEN: { value: ProblemKategorie; label: string; icon: string }[] = [
  { value: 'material',    label: 'Material',            icon: '🧱' },
  { value: 'masse',       label: 'Maße / Abmessung',    icon: '📐' },
  { value: 'zeichnung',   label: 'Zeichnung / Plan',    icon: '📋' },
  { value: 'oberflaeche', label: 'Oberfläche / Lack',   icon: '🎨' },
  { value: 'maschine',    label: 'Maschine / Werkzeug', icon: '⚙️' },
  { value: 'sonstige',    label: 'Sonstige',            icon: '❓' },
];

export function QSMeldenModal({ modul, recordId, bestellnr, kundeName, userId, abteilungId, onClose, onSaved }: Props) {
  const [kategorie, setKategorie] = useState<ProblemKategorie>('material');
  const [beschreibung, setBeschreibung] = useState('');
  const [priority, setPriority] = useState<ProblemPriority>('normal');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      await api.problems.create({
        module_table: modul,
        record_id: recordId,
        user_id: userId ?? undefined,
        abteilung_id: abteilungId,
        kategorie,
        beschreibung: beschreibung.trim() || undefined,
        priority,
      });
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
         onClick={onClose}>
      <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-xl"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-xs uppercase text-red-700 font-bold mb-1">⚠ Problem an QS melden</div>
            <h2 className="text-xl font-bold">{kundeName ?? 'Auftrag'} · {bestellnr}</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-2xl leading-none">×</button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded p-2 mb-3 text-sm">{error}</div>
        )}

        <label className="block text-xs font-semibold text-slate-600 mb-2">Kategorie</label>
        <div className="grid grid-cols-2 gap-2 mb-4">
          {KATEGORIEN.map(k => (
            <button key={k.value}
                    onClick={() => setKategorie(k.value)}
                    className={`text-left p-3 rounded-lg border text-sm transition ${
                      kategorie === k.value
                        ? 'bg-red-50 border-red-400 text-red-900 font-semibold'
                        : 'border-slate-200 hover:bg-slate-50'
                    }`}>
              <span className="mr-2">{k.icon}</span>{k.label}
            </button>
          ))}
        </div>

        <label className="block text-xs font-semibold text-slate-600 mb-1">Beschreibung</label>
        <textarea value={beschreibung} onChange={e => setBeschreibung(e.target.value)}
                  rows={3}
                  placeholder="Was ist das Problem? — z.B. &quot;Material falsch, 1.0038 statt 1.4301 geliefert&quot;"
                  className="input w-full mb-4" />

        <label className="block text-xs font-semibold text-slate-600 mb-2">Priorität</label>
        <div className="flex gap-2 mb-6">
          {(['normal','hoch','kritisch'] as ProblemPriority[]).map(p => (
            <button key={p} onClick={() => setPriority(p)}
                    className={`flex-1 py-2 rounded-lg border text-sm font-semibold capitalize transition ${
                      priority === p
                        ? p === 'kritisch' ? 'bg-red-600 text-white border-red-700'
                        : p === 'hoch'     ? 'bg-orange-500 text-white border-orange-600'
                        :                    'bg-slate-600 text-white border-slate-700'
                        : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}>
              {p}
            </button>
          ))}
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary">Abbrechen</button>
          <button onClick={submit} disabled={saving}
                  className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg px-5 py-2 font-semibold">
            {saving ? 'Sendet…' : '⚠ An QS senden'}
          </button>
        </div>
      </div>
    </div>
  );
}
